import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import {
  AccessTime,
  Storage,
  Add,
  ContentCopy,
  CheckCircle,
  Warning,
  Speed,
  Schedule,
  BookOnline,
  Autorenew,
} from "@mui/icons-material";
import { useReadUserSubscriptionQuery } from "api/endpoints/subscription";
import { useUploadPaymentReceiptMutation } from "api/endpoints/payment-receipt";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATH } from "router/router-constant/router.constant";
import { toast } from "react-toastify";
import {
  getPlanDisplayName,
  getStatusDisplayName,
  formatManualVpnConfig,
  isInventoryProviderType,
  SUBSCRIPTION_STATUSES,
  VpnProviderType,
  SubscriptionStatus,
} from "@dots-crm/contracts/client";
import * as SC from "./my-subscription.style";

type SubscriptionData = {
  _id: string;
  subscriptionId?: string;
  planType: string;
  price?: number;
  status?: string;
  providerType?: VpnProviderType;
  activationPending?: boolean;
  manualCategoryName?: string;
  subscriptionUrl?: string;
  subscriptionConfig?: string;
  providerPayload?: {
    type: "vmess" | "ssh";
    vmessUrl?: string;
    ssh?: { host: string; port: number; username: string; password: string };
  };
  crmStatus?: string;
  gigLimit?: number;
  timeLimit?: number;
  usedTraffic?: number;
  timeLeft?: number;
  createdAt: string;
  expiresAt?: string;
};

export const MySubscriptionComponent = () => {
  const {
    data: subscription,
    isLoading,
    error,
  } = useReadUserSubscriptionQuery();
  const [uploadReceipt, { isLoading: uploading }] =
    useUploadPaymentReceiptMutation();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [configCopied, setConfigCopied] = useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);

  const sub = subscription as SubscriptionData | null | undefined;
  const isInventory =
    !!sub?.providerType && isInventoryProviderType(sub.providerType);
  const isV2Ray = sub?.providerType === "V2RAY" || !isInventory;
  const subscriptionStatus = sub?.status as SubscriptionStatus | undefined;
  const isInventoryActive =
    isInventory && subscriptionStatus === SUBSCRIPTION_STATUSES.ACTIVE;
  const isInventoryDisabled =
    isInventory && subscriptionStatus === SUBSCRIPTION_STATUSES.DISABLED;
  const isInventoryPending =
    isInventory &&
    subscriptionStatus === SUBSCRIPTION_STATUSES.WAITING_FOR_REVIEW;
  const canShowInventoryCredentials = isInventoryActive;
  const purchasedDurationDays =
    sub?.timeLimit && sub.timeLimit > 0 ? sub.timeLimit * 30 : undefined;
  const providerLabel =
    sub?.providerType === "VMESS"
      ? "VMess"
      : sub?.providerType === "SSH"
        ? "SSH"
        : "V2Ray";

  const handleCopyText = async (text: string, onSuccess: () => void) => {
    try {
      await navigator.clipboard.writeText(text);
      onSuccess();
      toast.success("کپی شد");
      setTimeout(() => {
        setCopied(false);
        setConfigCopied(false);
      }, 2000);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.left = "-999999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(textarea);
      if (successful) {
        onSuccess();
        toast.success("کپی شد");
      } else {
        toast.error("خطا در کپی کردن");
      }
    }
  };

  const handleCopySubscriptionLink = async () => {
    const text = sub?.subscriptionUrl || sub?.subscriptionConfig;
    if (text) {
      await handleCopyText(text, () => setCopied(true));
    }
  };

  const handleCopyConfig = async () => {
    const config =
      sub?.subscriptionConfig ||
      (sub?.providerPayload ? formatManualVpnConfig(sub.providerPayload) : "");
    if (config) {
      await handleCopyText(config, () => setConfigCopied(true));
    }
  };

  const handleGuideClick = () => {
    navigate(ROUTE_PATH.subscriptions.guidance.path);
  };

  const handleRenewSubscription = () => {
    const currentPlan = sub?.planType;
    const currentGigLimit = sub?.gigLimit;
    const currentTimeLimit = sub?.timeLimit;

    const queryParams = new URLSearchParams({
      renew: "true",
      planType: currentPlan || "",
      gigLimit: currentGigLimit?.toString() || "0",
      timeLimit: currentTimeLimit?.toString() || "0",
    });

    navigate(
      `${ROUTE_PATH.subscriptions.purchase.path}?${queryParams.toString()}`
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setReceiptFile(file);
    }
  };

  const handleReuploadReceipt = async () => {
    if (!receiptFile) {
      toast.error("لطفاً فایل رسید پرداخت را انتخاب کنید");
      return;
    }

    if (!(sub as any)?.subscriptionId && !sub?._id) {
      toast.error("خطا در دریافت اطلاعات اشتراک");
      return;
    }

    try {
      await uploadReceipt({
        payload: {
          subscriptionId: (sub as any).subscriptionId || sub!._id,
          price: sub?.price || 0,
        },
        file: receiptFile,
      }).unwrap();

      toast.success(
        "رسید پرداخت با موفقیت آپلود شد. وضعیت اشتراک به 'در انتظار بررسی' تغییر کرد"
      );
      setReceiptFile(null);
      // Reset file input
      const fileInput = document.getElementById(
        "receipt-file-input"
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = "";
    } catch (error) {
      toast.error("خطا در آپلود رسید پرداخت");
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "active":
        return "success";
      case "disabled":
        return "error";
      case "limited":
        return "warning";
      case "expired":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case "active":
        return "فعال";
      case "disabled":
        return "غیرفعال";
      case "limited":
        return "محدود";
      case "expired":
        return "منقضی شده";
      default:
        return "نامشخص";
    }
  };

  const isSubscriptionExpired = () => {
    if (!sub?.expiresAt) return false;
    const expiryDate = new Date(sub.expiresAt);
    return expiryDate < new Date();
  };

  const canReuploadReceipt = () => {
    return sub?.status === "disabled" && !isSubscriptionExpired();
  };

  const calculateTrafficProgress = () => {
    if (!sub?.gigLimit || !sub?.usedTraffic) return 0;
    return Math.min((sub.usedTraffic / sub.gigLimit) * 100, 100);
  };

  const calculateTimeProgress = () => {
    if (!sub?.timeLimit || sub.timeLeft == null) return 0;
    const totalDays = sub.timeLimit * 30;
    const remainingDays = sub.timeLeft;
    return Math.max(
      Math.min(((totalDays - remainingDays) / totalDays) * 100, 100),
      0
    );
  };

  const formatPersianDate = (dateString?: string) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("fa-IR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getInventoryStatusLabel = () => {
    if (isInventoryActive) return "فعال";
    if (isInventoryDisabled) return "غیرفعال";
    if (isInventoryPending) return "در انتظار بررسی";
    return getStatusDisplayName(subscriptionStatus || SUBSCRIPTION_STATUSES.DISABLED);
  };

  const getInventoryStatusColor = (): "success" | "error" | "warning" | "default" => {
    if (isInventoryActive) return "success";
    if (isInventoryDisabled) return "error";
    if (isInventoryPending) return "warning";
    return "default";
  };

  if (isLoading) {
    return (
      <SC.SubscriptionContainer>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress size={60} />
        </Box>
      </SC.SubscriptionContainer>
    );
  }

  if (error) {
    return (
      <SC.SubscriptionContainer>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          خطا در بارگذاری اطلاعات اشتراک
        </Alert>
      </SC.SubscriptionContainer>
    );
  }

  if (!sub) {
    return (
      <SC.SubscriptionContainer>
        <SC.SubscriptionCard>
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <Box sx={{ mb: 3 }}>
              <Warning
                sx={{ fontSize: 64, color: "rgba(255, 255, 255, 0.8)" }}
              />
            </Box>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ color: "white", fontWeight: 600 }}
            >
              اشتراک فعالی ندارید
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "rgba(255, 255, 255, 0.8)", mb: 4 }}
            >
              برای استفاده از سرویس Velora، ابتدا یک اشتراک خریداری کنید.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add className="icon-btn" />}
              onClick={() => navigate(ROUTE_PATH.subscriptions.purchase.path)}
              size="large"
              sx={{
                background: "rgba(255, 255, 255, 0.2)",
                color: "white",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              خرید اشتراک جدید
            </Button>
          </CardContent>
        </SC.SubscriptionCard>
      </SC.SubscriptionContainer>
    );
  }

  const trafficProgress = calculateTrafficProgress();
  const timeProgress = calculateTimeProgress();
  const isTrafficUnlimited = !sub.gigLimit;
  const isTimeUnlimited = !sub.timeLimit;
  const manualConfigText =
    sub.subscriptionConfig ||
    (sub.providerPayload ? formatManualVpnConfig(sub.providerPayload) : "");

  return (
    <SC.SubscriptionContainer>
      <SC.SubscriptionHeader>
        <Typography
          variant="h3"
          gutterBottom
          sx={{ fontWeight: 700, color: "#333" }}
        >
          اشتراک من
        </Typography>
        <Typography variant="body1" color="text.secondary">
          مدیریت و نظارت بر اشتراک Velora شما
        </Typography>
      </SC.SubscriptionHeader>

      {isInventoryPending && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            در انتظار تأیید فیش پرداخت
          </Typography>
          <Typography variant="body2">
            فیش پرداخت شما در حال بررسی است. تا زمان تأیید ادمین، مشخصات اتصال
            (لینک VMess یا اطلاعات SSH) نمایش داده نمی‌شود.
            {sub.manualCategoryName && ` (تعرفه: ${sub.manualCategoryName})`}
          </Typography>
        </Alert>
      )}

      {isInventoryDisabled && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            اشتراک غیرفعال است
          </Typography>
          <Typography variant="body2">
            اشتراک {providerLabel} شما غیرفعال شده است و دسترسی به مشخصات اتصال
            وجود ندارد.
            {canReuploadReceipt()
              ? " می‌توانید رسید پرداخت جدید آپلود کنید."
              : " برای استفاده مجدد، اشتراک جدید خریداری کنید."}
          </Typography>
        </Alert>
      )}

      {/* Main Subscription Card */}
      <SC.SubscriptionCard>
        <CardContent sx={{ position: "relative", zIndex: 1 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={3}
          >
            <Box>
              <Typography variant="h5" sx={{ color: "white", fontWeight: 600 }}>
                {isInventory ? `اشتراک ${providerLabel}` : "اشتراک V2Ray شما"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.8)" }}
              >
                شناسه: {sub._id}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.8)" }}
              >
                {isInventory && sub.manualCategoryName
                  ? `تعرفه: ${sub.manualCategoryName}`
                  : `نوع پلن: ${getPlanDisplayName(sub.planType as any)}`}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255, 255, 255, 0.8)" }}
              >
                قیمت پرداخت شده: {sub.price?.toLocaleString()} تومان
              </Typography>
            </Box>
            {isV2Ray && (
            <SC.StatusChip
              label={getStatusText(sub.crmStatus)}
              color={getStatusColor(sub.crmStatus) as any}
              icon={
                sub.crmStatus === "active" ? (
                  <CheckCircle />
                ) : (
                  <Warning />
                )
              }
              sx={{ background: "rgba(255, 255, 255, 0.2)", color: "white" }}
            />
            )}
            {isInventory && (
              <SC.StatusChip
                label={getInventoryStatusLabel()}
                color={getInventoryStatusColor()}
                icon={
                  isInventoryActive ? <CheckCircle /> : <Warning />
                }
                sx={{ background: "rgba(255, 255, 255, 0.2)", color: "white" }}
              />
            )}
          </Box>

          {isInventory && (
            <Grid container spacing={3} sx={{ mb: isV2Ray ? 0 : undefined }}>
              <Grid item xs={12} md={6}>
                <SC.ProgressContainer>
                  <SC.ProgressLabel>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Schedule sx={{ color: "white" }} />
                      <Typography
                        variant="body1"
                        sx={{ color: "white", fontWeight: 600 }}
                      >
                        زمان باقی‌مانده
                      </Typography>
                    </Box>
                    {isInventoryActive && sub.timeLeft != null && sub.expiresAt ? (
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                      >
                        {sub.timeLeft} روز
                      </Typography>
                    ) : purchasedDurationDays ? (
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                      >
                        {sub.timeLimit} ماه ({purchasedDurationDays} روز)
                      </Typography>
                    ) : (
                      <SC.UnlimitedBadge>
                        <CheckCircle sx={{ fontSize: 16 }} />
                        نامحدود
                      </SC.UnlimitedBadge>
                    )}
                  </SC.ProgressLabel>
                  {isInventoryActive &&
                    sub.timeLimit &&
                    sub.timeLeft != null &&
                    sub.expiresAt && (
                      <SC.CustomProgress
                        variant="determinate"
                        value={calculateTimeProgress()}
                        sx={{
                          "& .MuiLinearProgress-bar": {
                            background:
                              calculateTimeProgress() > 80
                                ? "linear-gradient(90deg, #ff9800 0%, #f44336 100%)"
                                : "linear-gradient(90deg, #4caf50 0%, #8bc34a 100%)",
                          },
                        }}
                      />
                    )}
                </SC.ProgressContainer>
              </Grid>
              <Grid item xs={12} md={6}>
                <SC.ProgressContainer>
                  <SC.ProgressLabel>
                    <Box display="flex" alignItems="center" gap={1}>
                      <AccessTime sx={{ color: "white" }} />
                      <Typography
                        variant="body1"
                        sx={{ color: "white", fontWeight: 600 }}
                      >
                        تاریخ انقضا
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                    >
                      {isInventoryActive
                        ? formatPersianDate(sub.expiresAt)
                        : isInventoryPending
                        ? "پس از تأیید فیش"
                        : "—"}
                    </Typography>
                  </SC.ProgressLabel>
                </SC.ProgressContainer>
              </Grid>
            </Grid>
          )}

          {isV2Ray && (
          <Grid container spacing={3}>
            {/* Traffic Progress */}
            <Grid item xs={12} md={6}>
              <SC.ProgressContainer>
                <SC.ProgressLabel>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Speed sx={{ color: "white" }} />
                    <Typography
                      variant="body1"
                      sx={{ color: "white", fontWeight: 600 }}
                    >
                      ترافیک مصرفی
                    </Typography>
                  </Box>
                  {isTrafficUnlimited ? (
                    <SC.UnlimitedBadge>
                      <CheckCircle sx={{ fontSize: 16 }} />
                      نامحدود
                    </SC.UnlimitedBadge>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                    >
                      {sub.usedTraffic?.toFixed(2) || 0} / {sub.gigLimit} GB
                    </Typography>
                  )}
                </SC.ProgressLabel>
                {!isTrafficUnlimited && (
                  <SC.CustomProgress
                    variant="determinate"
                    value={trafficProgress}
                    sx={{
                      "& .MuiLinearProgress-bar": {
                        background:
                          trafficProgress > 80
                            ? "linear-gradient(90deg, #ff9800 0%, #f44336 100%)"
                            : "linear-gradient(90deg, #4caf50 0%, #8bc34a 100%)",
                      },
                    }}
                  />
                )}
              </SC.ProgressContainer>
            </Grid>

            {/* Time Progress */}
            <Grid item xs={12} md={6}>
              <SC.ProgressContainer>
                <SC.ProgressLabel>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Schedule sx={{ color: "white" }} />
                    <Typography
                      variant="body1"
                      sx={{ color: "white", fontWeight: 600 }}
                    >
                      زمان باقی‌مانده
                    </Typography>
                  </Box>
                  {isTimeUnlimited ? (
                    <SC.UnlimitedBadge>
                      <CheckCircle sx={{ fontSize: 16 }} />
                      نامحدود
                    </SC.UnlimitedBadge>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                    >
                      {sub.timeLeft || 0} روز
                    </Typography>
                  )}
                </SC.ProgressLabel>
                {!isTimeUnlimited && (
                  <SC.CustomProgress
                    variant="determinate"
                    value={timeProgress}
                    sx={{
                      "& .MuiLinearProgress-bar": {
                        background:
                          timeProgress > 80
                            ? "linear-gradient(90deg, #ff9800 0%, #f44336 100%)"
                            : "linear-gradient(90deg, #4caf50 0%, #8bc34a 100%)",
                      },
                    }}
                  />
                )}
              </SC.ProgressContainer>
            </Grid>
          </Grid>
          )}
        </CardContent>
      </SC.SubscriptionCard>

      {/* V2Ray copy link */}
      {isV2Ray && sub.subscriptionUrl && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <SC.CopyButton onClick={handleCopySubscriptionLink}>
            {copied ? <CheckCircle /> : <ContentCopy />}
            {copied ? "کپی شد!" : "کپی لینک اشتراک"}
          </SC.CopyButton>
        </Box>
      )}

      {/* VMess/SSH VPN config — only after receipt approval */}
      {canShowInventoryCredentials && manualConfigText && (
        <Box sx={{ mb: 3 }}>
          {sub.providerPayload?.type === "ssh" && (
            <Paper
              sx={{
                p: 2,
                mb: 2,
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                direction: "ltr",
                textAlign: "left",
                backgroundColor: "#f5f5f5",
              }}
            >
              {manualConfigText}
            </Paper>
          )}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <SC.CopyButton onClick={handleCopyConfig}>
              {configCopied ? <CheckCircle /> : <ContentCopy />}
              {configCopied
                ? "کپی شد!"
                : sub.providerPayload?.type === "vmess"
                ? "کپی لینک VMESS"
                : "کپی تنظیمات SSH"}
            </SC.CopyButton>
          </Box>
        </Box>
      )}

      {/* Stats Grid */}
      <SC.StatsGrid>
        <SC.StatItem>
          <Typography variant="h6" sx={{ color: "#333", fontWeight: 600 }}>
            {sub.gigLimit || "∞"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#666" }}>
            حجم کل (GB)
          </Typography>
        </SC.StatItem>
        {isV2Ray && (
        <SC.StatItem>
          <Typography variant="h6" sx={{ color: "#333", fontWeight: 600 }}>
            {sub.usedTraffic?.toFixed(2) || "0"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#666" }}>
            مصرف شده (GB)
          </Typography>
        </SC.StatItem>
        )}
        <SC.StatItem>
          <Typography variant="h6" sx={{ color: "#333", fontWeight: 600 }}>
            {sub.timeLimit || "∞"}
          </Typography>
          <Typography variant="body2" sx={{ color: "#666" }}>
            مدت (ماه)
          </Typography>
        </SC.StatItem>
        <SC.StatItem>
          <Typography variant="h6" sx={{ color: "#333", fontWeight: 600 }}>
            {isInventory && !isInventoryActive
              ? "—"
              : sub.timeLeft ?? (sub.timeLimit ? "—" : "∞")}
          </Typography>
          <Typography variant="body2" sx={{ color: "#666" }}>
            روز باقی‌مانده
          </Typography>
        </SC.StatItem>
      </SC.StatsGrid>

      {/* Action Buttons */}
      <SC.ActionGrid>
        <SC.GuideButton onClick={handleGuideClick}>
          <BookOnline />
          راهنمای استفاده
        </SC.GuideButton>
        <SC.GuideButton onClick={handleRenewSubscription}>
          <Autorenew />
          تمدید اشتراک
        </SC.GuideButton>
        {canReuploadReceipt() && (
          <SC.ReuploadButton
            onClick={handleReuploadReceipt}
            disabled={!receiptFile || uploading}
          >
            {uploading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Add />
            )}
            {uploading ? "در حال آپلود..." : "آپلود مجدد رسید پرداخت"}
          </SC.ReuploadButton>
        )}
      </SC.ActionGrid>

      {/* File Upload Section for Disabled Subscriptions */}
      {canReuploadReceipt() && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: "#fff3e0",
            borderRadius: 2,
            border: "1px solid #ffb74d",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              color: "#e65100",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Warning />
            آپلود مجدد رسید پرداخت
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: "#bf360c" }}>
            اشتراک شما به دلیل رد شدن رسید پرداخت غیرفعال شده است. لطفاً رسید
            پرداخت جدیدی آپلود کنید.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <input
              id="receipt-file-input"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <Button
              variant="outlined"
              component="label"
              htmlFor="receipt-file-input"
              sx={{
                borderColor: "#ff6b35",
                color: "#ff6b35",
                "&:hover": {
                  borderColor: "#e55a2b",
                  backgroundColor: "#fff3e0",
                },
              }}
            >
              انتخاب فایل
            </Button>
            {receiptFile && (
              <Typography variant="body2" sx={{ color: "#2e7d32" }}>
                فایل انتخاب شده: {receiptFile.name}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Expired Disabled Subscription Message */}
      {(sub?.status === "disabled") &&
        isSubscriptionExpired() && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: "#ffebee",
              borderRadius: 2,
              border: "1px solid #f44336",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: "#d32f2f",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Warning />
              اشتراک منقضی شده
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "#c62828" }}>
              اشتراک شما منقضی شده و غیرفعال است. برای استفاده مجدد از سرویس،
              لطفاً اشتراک جدیدی خریداری کنید.
            </Typography>
          </Box>
        )}

      {/* Additional Info Cards */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={4}>
          <SC.InfoCard>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <AccessTime color="primary" />
                اطلاعات زمانی
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  تاریخ خرید: {formatPersianDate(sub.createdAt)}
                </Typography>
                {isInventory && purchasedDurationDays && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    مدت اشتراک: {sub.timeLimit} ماه ({purchasedDurationDays}{" "}
                    روز)
                  </Typography>
                )}
                {isInventoryActive && sub.expiresAt && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    تاریخ انقضا: {formatPersianDate(sub.expiresAt)}
                  </Typography>
                )}
                {isInventoryPending && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    تاریخ انقضا پس از تأیید فیش محاسبه می‌شود.
                  </Typography>
                )}
                {!isInventory && sub.expiresAt && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    تاریخ انقضا: {formatPersianDate(sub.expiresAt)}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </SC.InfoCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <SC.InfoCard>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Storage color="primary" />
                اطلاعات ترافیک
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {isInventory
                    ? `وضعیت: ${getInventoryStatusLabel()}`
                    : `وضعیت: ${getStatusText(sub.crmStatus)}`}
                </Typography>
                {isInventory ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {canShowInventoryCredentials
                      ? "مشخصات اتصال: فعال و قابل استفاده"
                      : isInventoryDisabled
                      ? "مشخصات اتصال: غیرفعال"
                      : "مشخصات اتصال: پس از تأیید فیش نمایش داده می‌شود"}
                  </Typography>
                ) : (
                  sub.subscriptionUrl && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      لینک اشتراک: موجود
                    </Typography>
                  )
                )}
              </Box>
            </CardContent>
          </SC.InfoCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <SC.InfoCard>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <CheckCircle color="primary" />
                اطلاعات خرید
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {isInventory && sub.manualCategoryName
                    ? `تعرفه: ${sub.manualCategoryName}`
                    : `نوع پلن: ${getPlanDisplayName(sub.planType as any)}`}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  قیمت پرداخت شده: {sub.price?.toLocaleString()} تومان
                </Typography>
              </Box>
            </CardContent>
          </SC.InfoCard>
        </Grid>
      </Grid>
    </SC.SubscriptionContainer>
  );
};
